import styled from "@emotion/styled";
import { AnimatePresence } from "framer-motion";
import React from "react";
import { MapRef } from "react-map-gl/maplibre";
import { Page } from "../../activities/AppPage";
import { isLoading, Loading } from "../../Loading";
import {
  CaregiverStatus,
  PatientTaskBroadcastDetails,
  PatientTaskInstance,
  PatientTaskInstanceId,
  VisitBroadcastId,
} from "../../messages";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { AppPatientsState } from "../../system/AppState/AppPatientsState";
import { Visit } from "../../system/AppState/AppVisitsState";
import { getIsRN } from "../../utils/caregiverUtils";
import { fmap } from "../../utils/fmap";
import { KeyedList, List } from "../../utils/List";
import { setStatusBarAsBlackOnWhite } from "../../utils/statusBar";
import {
  filterPatientTask,
  filterPatientTaskBroadcast,
  getAssignedTaskStatus,
  getHasTaskPassed,
} from "../../utils/taskUtils";
import { filterVisit, isVisitPreferenceMismatch } from "../../utils/visitUtils";
import { getTaskStatus, getVisitStatus } from "../AvailableVisitsAndTasksList";
import SafeArea from "../Generic/SafeArea";
import Spinner from "../Generic/Spinner";
import PatientsFilterBottomSheet from "../PatientsFilterBottomSheet";
import useScheduledVisitsAvailableFilters from "../ScheduledVisits/useScheduledVisitsAvailableFilters";
import useScheduledVistsFilters from "../ScheduledVisits/useScheduledVistsFilters";
import usePendingConfirmationBannerProps from "../VisitPendingConfirmation/hooks/usePendingConfirmationBannerProps";
import { VisitPendingConfirmationBannerV2 } from "../VisitPendingConfirmation/VisitPendingConfirmationBannerV2";
import CaregiverMapCard from "./components/CaregiverMapCard";
import DismissableMainLayoutOverlay from "./components/DismissableMainLayoutOverlay";
import DismissableViewOverlay from "./components/DismissableViewOverlay";
import MainBanner, { MainBannerProps } from "./components/MainBanner";
import MainFooter from "./components/MainFooter";
import MainLayoutFabs from "./components/MainLayoutFabs";
import { MainLayoutView } from "./components/MainLayoutView";
import MainNavbar, { DefinedPlaceResult } from "./components/MainNavbar";
import Sidebar from "./components/Sidebar";
import TaskMapCard from "./components/TaskMapCard";
import VisitMapCard from "./components/VisitMapCard";
import useMainLayoutPreference from "./hooks/useMainLayoutPreference";
import { MainScreenActiveCard } from "./main-utils";

export type MainTab = "explore" | "my-visits" | "instapay" | "patients" | "chat";

type Props = {
  banners: MainBannerProps[];
  caregiver: AppCaregiver;
  caregiverStatusInMedflyt: CaregiverStatus | null;
  activeView: React.ReactNode | null;
  visits: List<Visit> | Loading;
  tasks: List<PatientTaskBroadcastDetails> | Loading;
  assignedTasks: List<PatientTaskInstance> | Loading;
  notificationsBadge: number;
  activeTab: MainTab;
  totalNewChats: number;
  patientsState: Loading | AppPatientsState;
  connectionStatus?: React.ReactNode;
  children?: React.ReactNode;
  onClickNotifications: () => void;
  onClickTab: (tab: MainTab) => void;
  onClickAdjustments: () => void;
  onClickVisit: (visit: Visit) => void;
  onClickTask: (taskInstanceId: PatientTaskInstanceId) => void;
  onClickAssignedTask: (taskInstanceId: PatientTaskInstanceId) => void;
  onRequestNavigate: (page: Page) => void;
  onRequestCloseView: () => void;
  onClickConfirmAssignment: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
};

export default function MainV2Screen(props: Props) {
  const [isSidebarOpen, setSidebarOpen] = React.useState(false);
  const [activeCard, setActiveCard] = React.useState<MainScreenActiveCard | null>(null);
  const [mapRef, setMapRef] = React.useState<MapRef | null>(null);
  const [cardHeight, setCardHeight] = React.useState<number | null>(null);
  const [searchedPlace, setSearchedPlace] = React.useState<DefinedPlaceResult | null>(null);
  const [mainLayout] = useMainLayoutPreference();
  const filters = useScheduledVistsFilters();
  const [showVisitsOutsideAvailability, setShowVisitsOutsideAvailability] = React.useState(false);

  const availableFilters = useScheduledVisitsAvailableFilters({
    patientTasks: isLoading(props.assignedTasks)
      ? List.empty()
      : props.assignedTasks.filter((task) => !getHasTaskPassed(task)),
    patientTaskBroadcasts: isLoading(props.tasks) ? List.empty() : props.tasks,
    visitPublicDetails: isLoading(props.visits)
      ? List.empty()
      : props.visits
          .filter((visit) => showVisitsOutsideAvailability || !isVisitPreferenceMismatch(visit))
          .map((x) => x.publicVisitBroadcastDetails),
  });

  const tasks = React.useMemo(
    () =>
      isLoading(props.tasks)
        ? props.tasks
        : props.tasks.filter((task) => filterPatientTaskBroadcast(task, filters)),
    [filters, props.tasks]
  );

  const assignedTasks = React.useMemo(
    () =>
      isLoading(props.assignedTasks)
        ? props.assignedTasks
        : props.assignedTasks
            .filter((task) => filterPatientTask(task, filters))
            .filter((task) => !getHasTaskPassed(task)),
    [filters, props.assignedTasks]
  );

  const visits = React.useMemo(
    () =>
      isLoading(props.visits)
        ? props.visits
        : props.visits
            .filter((visit) => filterVisit(visit.publicVisitBroadcastDetails, filters))
            .filter((visit) => !isVisitPreferenceMismatch(visit)),
    [filters, props.visits]
  );

  const visitsOutsideAvailability = React.useMemo(
    () =>
      isLoading(props.visits)
        ? props.visits
        : props.visits
            .filter((visit) => filterVisit(visit.publicVisitBroadcastDetails, filters))
            .filter(isVisitPreferenceMismatch),
    [filters, props.visits]
  );

  const patientsState = React.useMemo(() => {
    if (
      isLoading(props.patientsState) ||
      isLoading(props.visits) ||
      isLoading(props.tasks) ||
      isLoading(props.assignedTasks)
    )
      return props.patientsState;

    const patientIds = new Set(
      props.tasks
        .map((task) => task.patientId)
        .concat(
          props.visits
            .filter((visit) => showVisitsOutsideAvailability || !isVisitPreferenceMismatch(visit))
            .map((visit) => visit.publicVisitBroadcastDetails.patientId)
        )
        .concat(
          props.assignedTasks
            .filter((task) => !getHasTaskPassed(task))
            .map((task) => task.taskDetails.patientId)
        )
        .toArray()
    );

    return {
      patients: KeyedList.from(
        props.patientsState.patients.filter((patient) => patientIds.has(patient.id))
      )("id"),
    };
  }, [
    props.assignedTasks,
    props.patientsState,
    props.tasks,
    props.visits,
    showVisitsOutsideAvailability,
  ]);

  const cardRef = (ref: HTMLDivElement | null) => {
    if (ref) {
      setCardHeight(ref.clientHeight);
    }
  };

  React.useEffect(setStatusBarAsBlackOnWhite, []);

  React.useEffect(() => {
    if (mainLayout === "Cards") {
      setActiveCard(null);
      setCardHeight(null);
    }
  }, [mainLayout]);

  React.useEffect(() => {
    const timeout = setTimeout(() => mapRef?.resize(), 100);
    return () => clearTimeout(timeout);
  }, [mapRef, props.banners]);

  const handleClickMap = () => {
    setActiveCard(null);
    setCardHeight(null);

    mapRef?.flyTo({
      zoom: 14,
      pitch: 0,
      bearing: 0,
    });
  };

  const handleClickCaregiverMarker = (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    setActiveCard({ type: "caregiver" });

    mapRef?.flyTo({
      center: [props.caregiver.addressGeoLocation.lng, props.caregiver.addressGeoLocation.lat],
      zoom: 15,
      pitch: 45,
    });
  };

  const handleClickVisitMarker = (visit: Visit, e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    setActiveCard({ type: "visit", visit });

    mapRef?.flyTo({
      center: [
        visit.publicVisitBroadcastDetails.location.location.lng,
        visit.publicVisitBroadcastDetails.location.location.lat - 0.003,
      ],
      zoom: 15,
      pitch: 45,
    });
  };

  const handleClickTaskMarker = (
    task: PatientTaskBroadcastDetails,
    e: React.MouseEvent<Element>
  ) => {
    e.stopPropagation();
    setActiveCard({ type: "task", task });

    if (task.patientAddressComponents === null) {
      return;
    }

    mapRef?.flyTo({
      center: [
        task.patientAddressComponents.location.lng,
        task.patientAddressComponents.location.lat - 0.003,
      ],
      zoom: 15,
      pitch: 45,
    });
  };

  const handleClickAssignedTaskMarker = (
    task: PatientTaskInstance,
    e: React.MouseEvent<Element>
  ) => {
    e.stopPropagation();
    setActiveCard({ type: "assigned-task", task });

    if (task.taskDetails.patientAddressComponent === null) {
      return;
    }

    mapRef?.flyTo({
      center: [
        task.taskDetails.patientAddressComponent.location.lng,
        task.taskDetails.patientAddressComponent.location.lat - 0.003,
      ],
      zoom: 15,
      pitch: 45,
    });
  };

  const handleSearchPlace = (place: DefinedPlaceResult) => {
    setSearchedPlace(place);

    mapRef?.flyTo({
      center: [place.geometry.location.lng(), place.geometry.location.lat()],
      zoom: 15,
    });
  };

  const handleCloseCard = () => {
    setActiveCard(null);
    setCardHeight(null);
  };

  const pendingVisits = isLoading(visits)
    ? null
    : (visits.filter(
        (visit) => getVisitStatus(visit) === "PENDING_CONFIRMATION"
      ) as List<Visit.Available>);

  const pendingBannerProps = usePendingConfirmationBannerProps({
    visits: pendingVisits ?? List.empty(),
    onConfirmAssignmentClick: props.onClickConfirmAssignment,
  });

  const handleClickAdjustments = () => {
    if (getIsRN(props.caregiver.certifications)) {
      filters.open();
    } else {
      props.onClickAdjustments();
    }
  };

  const areCardsLoading =
    isLoading(visits) ||
    isLoading(visitsOutsideAvailability) ||
    isLoading(tasks) ||
    isLoading(assignedTasks);

  const root = (
    <MainRoot>
      <MainHeader top={true}>
        <AnimatePresence>
          {props.banners.map((banner, index) => (
            <MainBanner key={index} {...banner} />
          ))}
        </AnimatePresence>

        {pendingBannerProps !== null ? (
          <VisitPendingConfirmationBannerV2 {...pendingBannerProps} />
        ) : null}

        <MainNavbar
          caregiver={props.caregiver}
          patientsState={patientsState}
          notificationsBadge={props.notificationsBadge}
          filteredPatient={filters.patient}
          onClickNotifications={props.onClickNotifications}
          onClickAvatar={() => setSidebarOpen(true)}
          onSearchPlace={handleSearchPlace}
          onSearchPatient={filters.setPatient}
        />
      </MainHeader>

      {areCardsLoading && (
        <SpinnerContainer>
          <Spinner speed={0.75} size={32} />
        </SpinnerContainer>
      )}

      <DismissableViewOverlay isOpen={props.activeView !== null} onClose={props.onRequestCloseView}>
        {props.activeView}
      </DismissableViewOverlay>

      <MainLayoutWrapper data-layout={mainLayout}>
        {!areCardsLoading && (
          <AnimatePresence>
            <MainLayoutView
              caregiver={props.caregiver}
              visits={visits}
              visitsOutsideAvailability={visitsOutsideAvailability}
              tasks={tasks}
              assignedTasks={assignedTasks}
              activeCard={activeCard}
              mapRef={mapRef}
              searchedPlace={searchedPlace}
              setMapRef={setMapRef}
              raffle={props.caregiver.raffle}
              showVisitsOutsideAvailability={showVisitsOutsideAvailability}
              onToggleShowVisitsOutsideAvailability={() =>
                setShowVisitsOutsideAvailability((prev) => !prev)
              }
              onClickRaffleBanner={() => props.onRequestNavigate({ type: "Raffle" })}
              onClickMap={handleClickMap}
              onClickVisit={props.onClickVisit}
              onClickTask={props.onClickTask}
              onClickCaregiverMarker={handleClickCaregiverMarker}
              onClickVisitMarker={handleClickVisitMarker}
              onClickTaskMarker={handleClickTaskMarker}
              onClickAssignedTaskMarker={handleClickAssignedTaskMarker}
              onClickPreferenceSettings={handleClickAdjustments}
            />
          </AnimatePresence>
        )}

        <MainLayoutOverlay>
          <MainLayoutFabs
            filterCount={filters.count}
            offsetBottom={fmap(cardHeight, (x) => x + 8) ?? 0}
            onClickAdjustments={handleClickAdjustments}
            onClickHome={handleClickCaregiverMarker}
          />

          <DismissableMainLayoutOverlay
            isOpen={activeCard?.type === "visit"}
            onClose={handleCloseCard}
          >
            {activeCard?.type === "visit" && (
              <VisitMapCard
                key={activeCard.visit.id.toString()}
                ref={cardRef}
                caregiver={props.caregiver}
                visit={activeCard.visit}
                onClick={props.onClickVisit}
              />
            )}
          </DismissableMainLayoutOverlay>

          <DismissableMainLayoutOverlay
            isOpen={activeCard?.type === "task"}
            onClose={handleCloseCard}
          >
            {activeCard?.type === "task" && (
              <TaskMapCard
                key={activeCard.task.taskInstanceId.toString()}
                ref={cardRef}
                caregiver={props.caregiver}
                id={activeCard.task.taskInstanceId}
                status={getTaskStatus(activeCard.task)}
                title={activeCard.task.title}
                startDate={activeCard.task.startDate}
                dueDate={activeCard.task.dueDate}
                durationMinutes={activeCard.task.durationMinutes}
                patientInitials={activeCard.task.patientInitials}
                patientAddressComponents={activeCard.task.patientAddressComponents}
                patientAddress={activeCard.task.patientAddress}
                onClick={props.onClickTask}
              />
            )}
          </DismissableMainLayoutOverlay>

          <DismissableMainLayoutOverlay
            isOpen={activeCard?.type === "assigned-task"}
            onClose={handleCloseCard}
          >
            {activeCard?.type === "assigned-task" && (
              <TaskMapCard
                key={activeCard.task.id.toString()}
                ref={cardRef}
                caregiver={props.caregiver}
                id={activeCard.task.id}
                status={getAssignedTaskStatus(activeCard.task)}
                title={activeCard.task.taskDetails.title}
                startDate={activeCard.task.startDate}
                dueDate={activeCard.task.dueDate}
                durationMinutes={activeCard.task.taskDetails.durationMinutes}
                patientInitials={activeCard.task.taskDetails.patientInitials}
                patientAddressComponents={activeCard.task.taskDetails.patientAddressComponent}
                patientAddress={activeCard.task.taskDetails.patientAddress}
                onClick={props.onClickAssignedTask}
              />
            )}
          </DismissableMainLayoutOverlay>

          <DismissableMainLayoutOverlay
            isOpen={activeCard?.type === "caregiver"}
            onClose={handleCloseCard}
          >
            <CaregiverMapCard caregiver={props.caregiver} ref={cardRef} />
          </DismissableMainLayoutOverlay>
        </MainLayoutOverlay>
      </MainLayoutWrapper>

      {props.children}

      <MainFooter
        totalNewChats={props.totalNewChats}
        activeTab={props.activeTab}
        onClickTab={props.onClickTab}
      />

      <PatientsFilterBottomSheet
        isOpen={filters.isOpen}
        availablePatientSchedule={availableFilters.patientSchedule}
        availableBoroughs={availableFilters.boroughs}
        availableVisitTypes={availableFilters.visitTypes}
        boroughs={filters.boroughs}
        patientSchedule={filters.patientSchedule}
        visitTypes={filters.visitTypes}
        onChangeBoroughs={filters.setBoroughs}
        onChangePatientSchedule={filters.setPatientSchedule}
        onChangeVisitTypes={filters.setVisitTypes}
        onRequestClose={filters.close}
      />

      {props.connectionStatus}
    </MainRoot>
  );

  const sidebar = (
    <Sidebar
      isOpen={isSidebarOpen}
      caregiver={props.caregiver}
      onRequestNavigate={props.onRequestNavigate}
      onRequestClose={() => setSidebarOpen(false)}
    />
  );

  return (
    <>
      {sidebar}
      {root}
    </>
  );
}

const SpinnerContainer = styled.div`
  --size: 56px;
  background-color: white;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  margin: auto;
  transform: translateY(-50%);
  width: var(--size);
  height: var(--size);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const MainLayoutWrapper = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
  overflow: auto;

  &[data-layout="Map"] {
    --offset: calc(24px + 32px);
    margin: calc(var(--offset) * -1) 0;
  }
`;

const MainLayoutOverlay = styled.div`
  z-index: 1;
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  margin: calc(var(--offset)) 0;
  box-sizing: border-box;
  pointer-events: none;
  padding: 12px;

  > * {
    pointer-events: auto;
  }
`;

const MainHeader = styled(SafeArea)`
  background-color: white;
`;

const MainRoot = styled.div`
  background-color: var(--tw-slate-50);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  ${MainLayoutWrapper} {
    position: relative;
    z-index: 0;
  }

  ${MainNavbar.Root}, ${MainFooter.Root} {
    position: relative;
    z-index: 1;
  }
`;
